import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import pagePath from "../../../../util/constants/pagePath";
import getScoreColor from "../../../../util/functions/getScoreColor";
import "./practiceTestResult.css";

const PracticeTestResult = ({ examSummary, onBack = null }) => {
    const [reviewFilter, setReviewFilter] = useState(null);
    const navigate = useNavigate();
    const reviewRef = useRef(null);

    const counts = examSummary?.counts || {};
    const totalQuestions = counts.peq_total || 0;
    const totalCorrect = counts.peq_answered_pass || 0;
    const totalIncorrect = counts.peq_answered_fail || 0;
    const scoreFactor = counts.score_factor || 0;
    const scorePercent = Math.round(scoreFactor * 100);
    const averageTimeSeconds = counts.average_time_per_question_seconds || 0;
    const isFullTest = examSummary?.exam_type === "full";

    const domainResults = useMemo(() => {
        const domainMap = {};
        const questionList = examSummary?.practice_question_list || [];

        questionList.forEach((practiceQuestion) => {
            const domainId = practiceQuestion.question.cert_domain_id || "unknown";
            const domainName = practiceQuestion.question.domain_name || "Domain not available";

            if (!domainMap[domainId]) {
                domainMap[domainId] = {
                    domainId,
                    name: domainName,
                    correct: 0,
                    total: 0
                };
            }

            domainMap[domainId].total += 1;
            if (practiceQuestion.practice.question_state === "answer_pass") {
                domainMap[domainId].correct += 1;
            }
        });

        return Object.values(domainMap).sort((a, b) => {
            const aNum = Number((a.name.match(/domain\s+(\d+)/i) || [])[1]);
            const bNum = Number((b.name.match(/domain\s+(\d+)/i) || [])[1]);
            if (aNum && bNum && aNum !== bNum) return aNum - bNum;
            return a.name.localeCompare(b.name, undefined, { numeric: true });
        });
    }, [examSummary]);

    const filteredExamSummary = useMemo(() => {
        if (!reviewFilter) return null;

        const questionList = examSummary?.practice_question_list || [];
        let filteredQuestionList = questionList;
        if (reviewFilter.type === "correct") {
            filteredQuestionList = questionList.filter((practiceQuestion) => practiceQuestion.practice.question_state === "answer_pass");
        }
        if (reviewFilter.type === "incorrect") {
            filteredQuestionList = questionList.filter((practiceQuestion) => practiceQuestion.practice.question_state === "answer_fail");
        }
        if (reviewFilter.type === "domain") {
            filteredQuestionList = questionList.filter((practiceQuestion) => (practiceQuestion.question.cert_domain_id || "unknown") === reviewFilter.domainId);
        }

        return {
            ...examSummary,
            practice_question_list: filteredQuestionList
        };
    }, [examSummary, reviewFilter]);

    const handleBack = () => {
        if (onBack) {
            onBack();
            return;
        }
        navigate(pagePath.practice);
    }

    const formatSeconds = (seconds) => {
        if (!seconds || seconds < 1) return "0s";
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (remainingSeconds === 0) return `${minutes}m`;
        return `${minutes}m ${remainingSeconds}s`;
    }

    const getPerformanceTone = (factor) => {
        const percent = Math.round(Number(factor || 0) * 100);
        if (percent > 70) return "success";
        if (percent >= 40) return "warning";
        return "danger";
    }

    const summaryCards = [
        {
            label: "Total Questions",
            value: totalQuestions,
            icon: <AssignmentIcon />,
            tone: "neutral",
            reviewFilter: { type: "all", label: "All Questions" }
        },
        {
            label: "Total Correct",
            value: totalCorrect,
            icon: <CheckCircleIcon />,
            tone: "success",
            reviewFilter: { type: "correct", label: "Correct Questions" }
        },
        {
            label: "Total Incorrect",
            value: totalIncorrect,
            icon: <CancelIcon />,
            tone: "danger",
            reviewFilter: { type: "incorrect", label: "Incorrect Questions" }
        }
    ];

    const handleShowReview = (newReviewFilter) => {
        setReviewFilter(newReviewFilter);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const handleBackFromReview = () => {
        setReviewFilter(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const getOptionState = (practiceQuestion, option) => {
        if (practiceQuestion.question.correct_answer_op === option.option) return "correct";
        if (practiceQuestion.practice.answer_selection === option.option) return "incorrect";
        return "";
    }

    if (filteredExamSummary) {
        return (
            <div className="practice-test-result">
                <section className="practice-test-result-hero practice-test-result-review-hero">
                    <button className="practice-test-result-back-button" onClick={handleBackFromReview} aria-label="Back">
                        <ArrowBackIcon />
                    </button>
                    <div>
                        <h1>{reviewFilter.label}</h1>
                        <p>Review your test</p>
                    </div>
                </section>

                <section className="practice-test-review-list" ref={reviewRef}>
                    {filteredExamSummary.practice_question_list.map((practiceQuestion, index) => {
                        const isCorrect = practiceQuestion.practice.question_state === "answer_pass";
                        return (
                            <article key={practiceQuestion.practice.practice_exam_question_id} className="practice-test-review-card">
                                <h2>Question {index + 1} of {filteredExamSummary.practice_question_list.length}</h2>
                                <div className="practice-test-review-question-box">
                                    <p className="practice-test-review-question-text">{practiceQuestion.question.text}</p>
                                    <div className="practice-test-review-option-grid">
                                        {practiceQuestion.question.option_list.map((option) => {
                                            const optionState = getOptionState(practiceQuestion, option);
                                            return (
                                                <div key={option.option} className={`practice-test-review-option practice-test-review-option-${optionState}`}>
                                                    <span>{option.name}</span>
                                                    <strong>{option.option}</strong>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <div className={`practice-test-review-message ${isCorrect ? "practice-test-review-message-correct" : "practice-test-review-message-incorrect"}`}>
                                        {isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                                        <div>
                                            <strong>{isCorrect ? "Correct!" : "Incorrect"}</strong>
                                            <p>{isCorrect ? "Great job! You got it right." : "Review the correct answer and explanation below."}</p>
                                        </div>
                                    </div>
                                    {practiceQuestion.question.explanation ? (
                                        <div className="practice-test-review-explanation">
                                            <strong>Explanation:</strong>
                                            <p>{practiceQuestion.question.explanation}</p>
                                        </div>
                                    ) : null}
                                </div>
                            </article>
                        )
                    })}
                </section>
            </div>
        )
    }

    return (
        <div className="practice-test-result">
            <section className="practice-test-result-overview-card">
                <section className="practice-test-result-hero">
                    <button className="practice-test-result-back-button" onClick={handleBack} aria-label="Back">
                        <ArrowBackIcon />
                    </button>
                    <div>
                        <h1>Test Results</h1>
                        <p>Review your score and domain performance</p>
                    </div>
                </section>

                <section
                    className="practice-test-result-score-card"
                    style={{
                        "--score-percent": scorePercent,
                        "--score-color": getScoreColor(scorePercent)
                    }}
                >
                    <div className="practice-test-result-score-ring">
                        <div className="practice-test-result-score-ring-inner">
                            <strong>{scorePercent}%</strong>
                        </div>
                    </div>
                    <div className="practice-test-result-score-copy">
                        <p className="practice-test-result-score-label">
                            <EmojiEventsIcon /> Test Score
                        </p>
                        <p className="practice-test-result-score-meta">
                            <AccessTimeIcon /> Avg time: {formatSeconds(averageTimeSeconds)}
                        </p>
                    </div>
                </section>
            </section>

            <section className="practice-test-result-summary-grid">
                {summaryCards.map((card) => (
                    <div key={card.label} className="practice-test-result-summary-card">
                        <div className={`practice-test-result-summary-icon practice-test-result-tone-${card.tone}`}>
                            {card.icon}
                        </div>
                        <div>
                            <strong>{card.value}</strong>
                            <p>{card.label}</p>
                            <button onClick={() => handleShowReview(card.reviewFilter)}>
                                Review <KeyboardArrowRightIcon />
                            </button>
                        </div>
                    </div>
                ))}
            </section>

            {isFullTest ? (
                <section className="practice-test-result-domain-section">
                    <h2><BarChartIcon /> Domain-wise Performance</h2>
                    <div className="practice-test-result-domain-grid">
                        {domainResults.map((domain) => {
                            const domainFactor = domain.total > 0 ? domain.correct / domain.total : 0;
                            const domainPercent = Math.round(domainFactor * 100);
                            const tone = getPerformanceTone(domainFactor);
                            return (
                                <div
                                    key={domain.domainId}
                                    className="practice-test-result-domain-card"
                                    onClick={() => handleShowReview({ type: "domain", domainId: domain.domainId, label: domain.name })}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            handleShowReview({ type: "domain", domainId: domain.domainId, label: domain.name });
                                        }
                                    }}
                                >
                                    <div className="practice-test-result-domain-card-header">
                                        <strong>{domain.name}</strong>
                                        <span className={`practice-test-result-domain-percent practice-test-result-text-${tone}`}>
                                            {domainPercent}%
                                        </span>
                                    </div>
                                    <p>{domain.correct} correct <span>&bull;</span> {domain.total} total</p>
                                    <div className="practice-test-result-domain-progress">
                                        <div
                                            className={`practice-test-result-domain-progress-fill practice-test-result-bg-${tone}`}
                                            style={{ width: `${domainPercent}%` }}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </section>
            ) : null}

        </div>
    )
}

export default PracticeTestResult;
